import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  ChevronRight,
  Mail,
  Phone,
  ShieldCheck,
} from "lucide-react";
import { Badge, Button, Card, cn, useToast } from "@chotca/ui";
import type { UserMe } from "@chotca/sdk";
import { useMyIdentity, useRequestEmailVerify } from "@chotca/api-client";
import { PhoneVerifyDialog } from "./phone-verify-dialog";

// Account identity ladder surface on /profile (SCRUM-71). Shows the current
// level and the two contact channels (email, phone); phone opens the verify
// dialog. The CCCD row reflects the real submission status (not the aggregate
// level), so an approved CCCD reads "Đã xác minh" here even before U2.
export function ContactChannelsCard({
  user,
  contactPhoneFallback,
}: {
  user: UserMe;
  contactPhoneFallback?: string | null;
}) {
  const nav = useNavigate();
  const { toast } = useToast();
  const [phoneOpen, setPhoneOpen] = React.useState(false);
  const identity = useMyIdentity();
  const resendEmail = useRequestEmailVerify();

  const emailVerified = !!user.emailVerifiedAt;
  const phoneVerified = !!user.phoneVerifiedAt;
  const level = user.identityLevel ?? "U0_NEW";
  // Real CCCD submission status drives the CCCD row, independent of `level`.
  const cccdStatus = identity.data?.status ?? "NONE";
  const cccdApproved = cccdStatus === "APPROVED";
  const cccdPending = cccdStatus === "PENDING";

  return (
    <Card className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} strokeWidth={1.7} className="text-ash" />
          <span className="text-sm font-extrabold text-ink">
            Xác minh tài khoản
          </span>
        </div>
        <LevelBadge level={level} />
      </div>

      <div className="space-y-2">
        <ChannelRow
          icon={<Mail size={16} strokeWidth={1.7} />}
          label={user.email ?? "Chưa có email"}
          verified={emailVerified}
          actionLabel={resendEmail.isPending ? "Đang gửi…" : "Gửi lại email"}
          onAction={() => {
            if (resendEmail.isPending) return;
            resendEmail.mutate(undefined, {
              onSuccess: () =>
                toast({
                  title: "Đã gửi email xác minh",
                  description: `Kiểm tra hộp thư ${user.email ?? ""} và bấm vào liên kết.`,
                  tone: "success",
                }),
              onError: () =>
                toast({
                  title: "Không gửi được email. Vui lòng thử lại.",
                  tone: "error",
                }),
            });
          }}
          hideActionWhenVerified
        />
        <ChannelRow
          icon={<Phone size={16} strokeWidth={1.7} />}
          label={displayPhone(user.phone ?? contactPhoneFallback)}
          verified={phoneVerified}
          actionLabel={phoneVerified ? "Đổi số" : "Xác minh số điện thoại"}
          onAction={() => setPhoneOpen(true)}
        />
      </div>

      {cccdApproved ? (
        // Approved → not actionable. Show a static "verified" row instead of a
        // CTA so it can't be re-opened as if it still needs work.
        <div className="flex w-full items-center justify-between rounded-xl bg-lime/10 px-3 py-2.5">
          <span className="text-sm font-semibold text-ink">
            Định danh cá nhân (CCCD)
          </span>
          <span className="flex items-center gap-1 text-xs font-semibold text-lime-dark">
            <CheckCircle2 size={14} /> Đã xác minh
          </span>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => nav("/profile/identity")}
          className="flex w-full items-center justify-between rounded-xl bg-mist px-3 py-2.5 text-left transition-colors hover:bg-smoke"
        >
          <span className="text-sm font-semibold text-ink">
            Định danh cá nhân (CCCD)
          </span>
          <span className="flex items-center gap-1 text-xs font-semibold text-ash">
            {cccdPending ? "Đang duyệt" : "Nộp hồ sơ"}
            <ChevronRight size={14} />
          </span>
        </button>
      )}

      <PhoneVerifyDialog
        open={phoneOpen}
        onClose={() => setPhoneOpen(false)}
        initialPhone={user.phone ?? contactPhoneFallback}
      />
    </Card>
  );
}

function LevelBadge({ level }: { level: string }) {
  if (level === "U2_IDENTITY")
    return <Badge tone="lime-soft">Đã định danh</Badge>;
  if (level === "U1_CONTACT")
    return <Badge tone="mist">Đã xác minh liên hệ</Badge>;
  return <Badge tone="mist">Chưa xác minh</Badge>;
}

function ChannelRow({
  icon,
  label,
  verified,
  actionLabel,
  onAction,
  hideActionWhenVerified,
}: {
  icon: React.ReactNode;
  label: string;
  verified: boolean;
  actionLabel: string;
  onAction: () => void;
  hideActionWhenVerified?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-mist text-ash">
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm text-ink">{label}</span>
      {verified ? (
        <span
          className={cn(
            "flex items-center gap-1 text-xs font-semibold text-lime-dark",
          )}
        >
          <CheckCircle2 size={14} /> Đã xác minh
        </span>
      ) : hideActionWhenVerified && verified ? null : (
        <Button variant="link" size="sm" onClick={onAction} type="button">
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

function displayPhone(phone: string | null | undefined): string {
  if (!phone) return "Chưa có số điện thoại";
  const d = phone.replace(/\D/g, "");
  return d.startsWith("84") ? "0" + d.slice(2) : d;
}
